import { useMemo } from 'react'
import { useDebounce } from 'use-debounce'

import { spaceEveryThousand } from '~/bootcamp/Activities/Multiplication/MultiBy/constants'
import { useLearningStatsQuery } from '~/redux/apis/user-stats-api'
import { useAppSelector } from '~/redux/hooks'
import { convertMsToDayHourMinute } from '~/user-stats/utility/date.utils'

import { selectLearningStatsFilter } from '../redux/statistics.slice'
import { APIExecutionStepsType, DaysHoursMinutes } from '../types/common.types'
import { formatLearningStats } from '../utils/format-stat.utils'
import { getFilterDates } from '../utils/get-filter-date.utils'

interface ReturnType {
  activitiesDone: string
  activityAccuracy: string
  bestActivityAccuracy: string
  totalTimeSpent: DaysHoursMinutes
  videoWatchDuration: DaysHoursMinutes
  APIExecutionSteps: APIExecutionStepsType
  videoWatchDurationInMs: number
}

export default function useLearningStats({ familyMemberId }: { familyMemberId?: number }): ReturnType {
  const { timeFilter, productCategoryFilter } = useAppSelector(selectLearningStatsFilter)
  const filter = useMemo(() => {
    const [startDate, endDate] = getFilterDates(timeFilter)

    return {
      startDate: startDate?.toISOString(),
      endDate: endDate?.toISOString(),
      category: productCategoryFilter,
    }
  }, [timeFilter, productCategoryFilter])

  const [debouncedFilter] = useDebounce(filter, 500)

  const { data: learningStatsAPI, ...APIExecutionSteps } = useLearningStatsQuery({
    ...debouncedFilter,
    familyMemberId,
  })

  const learningStats = formatLearningStats(learningStatsAPI)

  const activitiesDone = spaceEveryThousand(learningStats.activitiesDone || 0)
  const activityAccuracy = spaceEveryThousand(learningStats.activityAccuracy || 0)
  const bestActivityAccuracy = spaceEveryThousand(learningStats.bestActivityAccuracy || 0)
  const totalTimeSpent = convertMsToDayHourMinute(learningStats.totalTimeSpent || 0)
  const videoWatchDuration = convertMsToDayHourMinute(learningStats.watchedVideoDuration || 0)
  const videoWatchDurationInMs = learningStats.watchedVideoDuration || 0

  return {
    activitiesDone,
    activityAccuracy,
    bestActivityAccuracy,
    totalTimeSpent,
    videoWatchDuration,
    APIExecutionSteps,
    videoWatchDurationInMs,
  }
}
